import { useEffect, useRef } from 'react';

import { subscribeErpRefresh } from './refresh';

/** Revalidate displayed data without remounting its editor. Loaders own their error
 * presentation and must return their request promise; this prevents overlapping polls.
 * Initial loads and actual scope changes remain the container's responsibility. */
export function useErpRefresh(
  refresh: (signal: AbortSignal) => Promise<unknown> | void,
  enabled = true
) {
  const latest = useRef(refresh);
  latest.current = refresh;
  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    let running = false;
    let again = false;
    const run = () => {
      if (controller.signal.aborted) return;
      if (running) {
        again = true;
        return;
      }
      running = true;
      Promise.resolve()
        .then(() => {
          if (!controller.signal.aborted)
            return latest.current(controller.signal);
        })
        .catch(() => {
          /* The loader surfaces failures; the scheduler keeps retrying. */
        })
        .finally(() => {
          running = false;
          if (again) {
            again = false;
            // Schedule through the visible/online check rather than polling hidden tabs.
            if (
              !controller.signal.aborted &&
              document.visibilityState === 'visible' &&
              navigator.onLine
            )
              run();
          }
        });
    };
    const release = subscribeErpRefresh(run);
    return () => {
      controller.abort();
      release();
    };
  }, [enabled]);
}
